import { getSelectedBrand, parseBrandList, PROJECT_CATCH_ERROR_LIST } from "@platformx/utilities";
import graphqlInstance from "../../config/graphqlConfig";
import {
  CREATE_CONTENT_TYPE,
  PUBLISH_CONTENT,
  PUBLISH_CONTENT_TYPE,
  SAVE_CONTENT_TYPE,
  UPDATE_CONTENT,
} from "../../graphQL/mutations/contentTypeMutations";
import { schemaQueries } from "../../graphQL/queries/contentTypeQueries";
import { ApiResponse } from "../../utils/types";

// brand_name is a system field on schema-based documents (accepted without the schema declaring
// it), same as on standard content types: keep an existing value; the selected brand only seeds a
// document that has none. secondary_brandname is never touched here — only Share With Brands sets
// it. The legacy brand_name_x_str keeps its old behaviour when a schema still declares it.
const appendSelectedBrandForSchemaField = (contentInstance: any, isShared = false) => {
  const input = { ...contentInstance };
  if (isShared) return input;

  const selectedBrand = getSelectedBrand();
  if (Object.hasOwn(input, "brand_name_x_str")) {
    const existing = input.brand_name_x_str;
    input.brand_name_x_str =
      parseBrandList(existing).length > 0 ? existing : `${selectedBrand || ""}`;
  }
  if (selectedBrand || input.brand_name) {
    input.brand_name = input.brand_name || selectedBrand;
  }
  return input;
};

const contentTypeSchemaApi = {
  getSchemaDynamic: async <T>(input: any): Promise<ApiResponse<T>> => {
    try {
      const { data } = await graphqlInstance.query({
        query: schemaQueries.CONTENT_SCHEMA,
        fetchPolicy: "no-cache",
        variables: input,
      });
      return data;
    } catch (err: any) {
      console.error(`${PROJECT_CATCH_ERROR_LIST.authoring}:getSchema`, err);
      return err?.response?.data || {};
    }
  },
  getSchemaDynamicWithData: async <T>(input: any): Promise<ApiResponse<T>> => {
    try {
      const { data } = await graphqlInstance.query({
        query: schemaQueries.CONTENT_SCHEMA_WITH_DATA,
        fetchPolicy: "no-cache",
        variables: input,
      });
      return data;
    } catch (err: any) {
      console.error(`${PROJECT_CATCH_ERROR_LIST.authoring}:getSchema`, err);
      return err?.response?.data || {};
    }
  },
  saveContent: async <T>(input: any, isShared = false): Promise<ApiResponse<T>> => {
    try {
      const { data } = await graphqlInstance.query({
        query: SAVE_CONTENT_TYPE,
        variables: {
          ...input,
          input: appendSelectedBrandForSchemaField(input?.input, isShared),
        },
        context: {
          skipBrandNameHeader: isShared,
        },
        fetchPolicy: "no-cache",
      });
      return data;
    } catch (err: any) {
      console.error(`${PROJECT_CATCH_ERROR_LIST.authoring}:saveContent`, err);
      return err?.response?.data || {};
    }
  },
  updateContent: async <T>(input: any, isShared = false): Promise<ApiResponse<T>> => {
    try {
      const { data } = await graphqlInstance.query({
        query: UPDATE_CONTENT,
        variables: {
          ...input,
          input: appendSelectedBrandForSchemaField(input?.input, isShared),
        },
        context: {
          skipBrandNameHeader: isShared,
        },
        fetchPolicy: "no-cache",
      });
      return data;
    } catch (err: any) {
      console.error(`${PROJECT_CATCH_ERROR_LIST.authoring}:saveContent`, err);
      return err?.response?.data || {};
    }
  },
  publishContentType: async <T>(input: any): Promise<ApiResponse<T>> => {
    try {
      const { data } = await graphqlInstance.query({
        query: PUBLISH_CONTENT,
        variables: input,
        fetchPolicy: "no-cache",
      });
      return data;
    } catch (err: any) {
      console.error(`${PROJECT_CATCH_ERROR_LIST.authoring}:publishContent`, err);
      return err?.response?.data || {};
    }
  },
  //start: old apis need to check profile and delte
  getSchema: async <T>(): Promise<ApiResponse<T>> => {
    try {
      const { data } = await graphqlInstance.query({
        query: schemaQueries.CONTENT_TYPE_SCHEMA,
        fetchPolicy: "no-cache",
      });
      return data;
    } catch (err: any) {
      console.error(`${PROJECT_CATCH_ERROR_LIST.authoring}:getSchema`, err);
      throw err;
    }
  },
  createContent: async <T>(input: any, isShared = false): Promise<ApiResponse<T>> => {
    try {
      const { data } = await graphqlInstance.query({
        query: CREATE_CONTENT_TYPE,
        variables: input,
        context: {
          skipBrandNameHeader: isShared,
        },
        fetchPolicy: "no-cache",
      });
      return data;
    } catch (err: any) {
      console.error(`${PROJECT_CATCH_ERROR_LIST.authoring}:createContent`, err);
      throw err;
    }
  },
  publishContent: async <T>(input: any): Promise<ApiResponse<T>> => {
    try {
      const { data } = await graphqlInstance.query({
        query: PUBLISH_CONTENT_TYPE,
        variables: input,
        fetchPolicy: "no-cache",
      });
      return data;
    } catch (err: any) {
      console.error(`${PROJECT_CATCH_ERROR_LIST.authoring}:publishContent`, err);
      throw err;
    }
  },
  fetchSchema: async <T>(): Promise<ApiResponse<T>> => {
    try {
      const { data } = await graphqlInstance.query({
        query: schemaQueries.FETCH_SCHEMA,
        fetchPolicy: "no-cache",
      });
      return data;
    } catch (err: any) {
      console.error(`${PROJECT_CATCH_ERROR_LIST.authoring}:fetchSchema`, err);
      return err?.response?.data || {};
    }
  },
  //end: old apis need to check profile and delte
};
export default contentTypeSchemaApi;
